import { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import AtelierCard from "../components/AtelierCard";
import { Input } from "../components/ui/input";
import { Search, Filter, X, BookOpenCheck, RotateCcw, Sparkles, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const DOMAIN_CODES = ["BHV", "FND", "O2O", "INF", "REC", "INV", "CRO", "PRE", "TEM"];
const FORMAT_CODES = ["C", "L", "D"];

export default function CataloguePage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isCEO = user?.role === "ceo";
  const [ateliers, setAteliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [domain, setDomain] = useState(null);
  const [format, setFormat] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [acting, setActing] = useState(false);
  // Semantic search state
  const [aiMode, setAiMode] = useState(false);
  const [aiResults, setAiResults] = useState(null); // null = not yet, [] = no match, [{atelier, score}]
  const [aiLoading, setAiLoading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const aiTimer = useRef(null);

  const refresh = () => {
    setLoading(true);
    return Promise.all([
      api.get("/ateliers").then((r) => setAteliers(r.data)),
      isCEO ? api.get("/admin/ateliers/stats").then((r) => setStats(r.data)).catch(() => {}) : Promise.resolve(),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCEO]);

  const publishAll = async () => {
    if (!window.confirm(t("catalogue.confirm_publish_all"))) return;
    setActing(true);
    try {
      const { data } = await api.post("/admin/ateliers/publish-all");
      toast.success(t("catalogue.publish_success", { n: data.updated }));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("catalogue.action_impossible"));
    } finally {
      setActing(false);
    }
  };

  const reimport = async () => {
    if (!window.confirm(t("catalogue.confirm_reimport"))) return;
    setActing(true);
    try {
      const { data } = await api.post("/admin/ateliers/reimport-catalogue?publish=true");
      toast.success(t("catalogue.reimport_success", { i: data.inserted, u: data.updated }));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("catalogue.action_impossible"));
    } finally {
      setActing(false);
    }
  };

  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";

  // Debounced semantic search : hits the server 350 ms after the user stops typing.
  useEffect(() => {
    if (!aiMode) {
      setAiResults(null);
      return;
    }
    if (aiTimer.current) clearTimeout(aiTimer.current);
    const q = query.trim();
    if (q.length < 2) {
      setAiResults(null);
      return;
    }
    aiTimer.current = setTimeout(async () => {
      setAiLoading(true);
      try {
        const { data } = await api.get("/ateliers-search", { params: { q, lang: uiLocale, limit: 15 } });
        setAiResults(data.results || []);
        if (!data.index_ready && isCEO) {
          toast.info(t("catalogue.index_not_ready", "L'index sémantique n'est pas encore construit. Cliquez sur « Ré-indexer » pour l'activer."));
        }
      } catch (_) {
        setAiResults([]);
      } finally {
        setAiLoading(false);
      }
    }, 350);
    return () => aiTimer.current && clearTimeout(aiTimer.current);
  }, [query, aiMode, uiLocale, isCEO, t]);

  const reindexEmbeddings = async () => {
    setReindexing(true);
    try {
      const { data } = await api.post("/admin/ateliers/reindex-embeddings");
      toast.success(t("catalogue.reindex_success", { fr: data.fr, en: data.en }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("catalogue.action_impossible"));
    } finally {
      setReindexing(false);
    }
  };

  const filtered = useMemo(() => {
    return ateliers.filter((a) => {
      if (domain && a.domain !== domain) return false;
      if (format && a.format !== format) return false;
      if (query) {
        const q = query.toLowerCase();
        return (
          a.title_fr?.toLowerCase().includes(q) ||
          a.title_en?.toLowerCase().includes(q) ||
          a.id.toLowerCase().includes(q) ||
          a.description?.toLowerCase().includes(q) ||
          a.symptomes_traites?.some((s) => s.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [ateliers, query, domain, format]);

  return (
    <div className="space-y-6" data-testid="catalogue-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">{t("catalogue.header_kicker")}</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">{t("catalogue.header_title")}</h1>
        <p className="font-oracle italic text-lg text-text-secondary mt-2">{t("catalogue.header_subtitle", { count: ateliers.length })}</p>
        {isCEO && stats && (
          <div className="mt-4 flex flex-wrap items-center gap-3 p-4 rounded-xl bg-bg-soft border border-border" data-testid="catalogue-admin-panel">
            <div className="text-xs font-bold uppercase tracking-wider text-text-secondary">{t("catalogue.admin")}</div>
            <div className="flex gap-2 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-white border border-border" data-testid="stat-total">{t("catalogue.stat_total", { n: stats.total })}</span>
              <span className="px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-800" data-testid="stat-published">{t("catalogue.stat_published", { n: stats.by_status.published })}</span>
              <span className="px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800" data-testid="stat-draft">{t("catalogue.stat_draft", { n: stats.by_status.draft })}</span>
            </div>
            <div className="flex gap-2 ml-auto">
              {stats.by_status.draft > 0 && (
                <Button size="sm" variant="outline" onClick={publishAll} disabled={acting} data-testid="btn-publish-all">
                  <BookOpenCheck className="w-3.5 h-3.5 mr-1.5" /> {t("catalogue.btn_publish_all")}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={reindexEmbeddings} disabled={reindexing} data-testid="btn-reindex-embeddings">
                {reindexing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {t("catalogue.btn_reindex")}
              </Button>
              <Button size="sm" variant="outline" onClick={reimport} disabled={acting} data-testid="btn-reimport">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> {t("catalogue.btn_reimport")}
              </Button>
            </div>
          </div>
        )}
      </header>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          {aiLoading ? (
            <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-apollo-orange animate-spin" />
          ) : (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
          )}
          <Input
            type="text"
            placeholder={aiMode ? t("catalogue.ai_placeholder", "Décris ce que tu cherches en une phrase…") : t("catalogue.search_placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 pr-32 h-11"
            data-testid="catalogue-search"
          />
          <button
            type="button"
            onClick={() => setAiMode(!aiMode)}
            data-testid="btn-toggle-ai-search"
            className={`absolute right-2 top-1/2 -translate-y-1/2 h-8 px-3 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
              aiMode
                ? "bg-apollo-orange text-white shadow-sm"
                : "bg-bg-soft text-text-secondary hover:bg-apollo-orange-light hover:text-apollo-orange-dark"
            }`}
            title={t("catalogue.ai_toggle_hint", "Recherche par sens (IA)")}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t("catalogue.ai_toggle", "IA")}
          </button>
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="md:w-auto" data-testid="btn-toggle-filters">
          <Filter className="w-4 h-4 mr-2" /> {t("catalogue.filters")}
          {(domain || format) && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-apollo-orange text-white text-xs">{[domain, format].filter(Boolean).length}</span>}
        </Button>
      </div>

      {showFilters && (
        <div className="bg-bg-soft rounded-xl p-4 space-y-3" data-testid="filters-panel">
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">{t("catalogue.domain")}</div>
            <div className="flex flex-wrap gap-2">
              {DOMAIN_CODES.map((d) => (
                <button
                  key={d}
                  onClick={() => setDomain(domain === d ? null : d)}
                  data-testid={`filter-domain-${d}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                    domain === d ? "bg-apollo-orange text-white" : "bg-white border border-border text-text-secondary hover:border-apollo-orange/40"
                  }`}
                >
                  {d} · {t(`catalogue.domains.${d}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">{t("catalogue.format")}</div>
            <div className="flex flex-wrap gap-2">
              {FORMAT_CODES.map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(format === f ? null : f)}
                  data-testid={`filter-format-${f}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                    format === f ? "bg-apollo-orange text-white" : "bg-white border border-border text-text-secondary hover:border-apollo-orange/40"
                  }`}
                >
                  {t(`catalogue.formats.${f}`)}
                </button>
              ))}
            </div>
          </div>
          {(domain || format) && (
            <Button variant="ghost" size="sm" onClick={() => { setDomain(null); setFormat(null); }} data-testid="btn-clear-filters">
              <X className="w-3 h-3 mr-1" /> {t("catalogue.clear")}
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-bg-soft rounded-xl animate-pulse" />)}
        </div>
      ) : aiMode && aiResults !== null ? (
        aiResults.length === 0 ? (
          <div className="text-center py-20 text-text-secondary" data-testid="ai-empty-state">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>{query.length < 2 ? t("catalogue.ai_type_more", "Écris au moins 2 caractères…") : t("catalogue.ai_empty", "Rien de pertinent trouvé : essaie une reformulation.")}</p>
          </div>
        ) : (
          <div>
            <p className="text-xs text-text-secondary mb-3 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-apollo-orange" />
              {t("catalogue.ai_result_hint", "{{n}} ateliers classés par pertinence sémantique", { n: aiResults.length })}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-results-grid">
              {aiResults.map((r) => (
                <div key={r.atelier.id} className="relative">
                  <div className="absolute -top-2 -right-2 z-10 bg-apollo-orange text-white text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full shadow-md" data-testid={`ai-score-${r.atelier.id}`}>
                    {Math.round(r.score * 100)}%
                  </div>
                  <AtelierCard atelier={r.atelier} titleLocale={uiLocale} />
                </div>
              ))}
            </div>
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-text-secondary" data-testid="empty-state">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{t("catalogue.empty")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ateliers-grid">
          {filtered.map((a) => <AtelierCard key={a.id} atelier={a} titleLocale={uiLocale} />)}
        </div>
      )}
    </div>
  );
}
